import { z } from 'zod';

import type * as Claim from './claim';
import {
  CreateClaimRequestSchema,
  CreateClaimSuccessResponseSchema,
} from './claim';
import type { ClaimResponseSchema } from './claim-response';
import {
  CreateClaimReversalRequestSchema,
  type CreateClaimReversalResponseSchema,
  CreateClaimReversalSuccessResponseSchema,
} from './claim-reversal';

export * from './ClaimClient';
export * from './Client';
export * from './claim';
export * from './claim-response';
export * from './claim-reversal';
export * as HealthbridgeExample from './example';
export * from './FamilyCheckClient';
export * from './medical-aid';
export * from './person';

export const AdministratorSchema = z.object({
  schemeName: z.string(),
  schemeCode: z.string(),
  canSearchOnIDNumber: z.boolean(),
});

export type Administrator = z.infer<typeof AdministratorSchema>;

export const FamilyCheckMembershipNumberRequestSchema = z.object({
  schemeCode: z.string(),
  membershipNumber: z.string(),
});

export const FamilyCheckIdNumberRequestSchema = z.object({
  schemeCode: z.string(),
  idNumber: z.string(),
});

export type FamilyCheckMembershipNumberRequest = z.infer<
  typeof FamilyCheckMembershipNumberRequestSchema
>;

/**
 * Will only work if the scheme allows ID number lookups
 */
export type FamilyCheckIdNumberRequest = z.infer<
  typeof FamilyCheckIdNumberRequestSchema
>;

/**
 * A family check query must only contain the membership number or id number - if the membership
 * number is defined then we should default to this since some schemes don't allow id number checks
 *
 * The resolution favors membership numbers since it's always a safe default regardless of the
 * scheme
 *
 * > We can assume the consumer has verified whether or not the scheme being used supports id number
 * checks
 */
export type FamilyCheckRequest =
  | FamilyCheckMembershipNumberRequest
  | FamilyCheckIdNumberRequest;

export const FamilyCheckRequestSchema = z.union([
  FamilyCheckIdNumberRequestSchema,
  FamilyCheckMembershipNumberRequestSchema,
]);

const AllowedDependencyType = [
  'Adult dependant',
  'Child',
  'Principle party',
  'Spouse',
] as const;

const DependencyTypeSchema = z.enum(AllowedDependencyType);
/**
 * The date fields are strings because that is what is sent to us. If the date is not present
 * in the response from the scheme we get a "No Record" returned. If valid, should be in the form
 * YYYY-MM-DD
 */

export const ResponseDetailsSchema = z.object({
  disclaimer: z.string(),
  responseDateTime: z.date(),
  enquiryNumber: z.string(),
  referenceNumber: z.string(),
});

export const PatientDetailSchema = z.object({
  membershipNumber: z.string(),
  schemeName: z.string(),
  plan: z.string(),
  dependantCode: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  /**
   * Can be any of these:
   *
   * The membership is valid and active
   * The membership is not valid
   * The membership is suspended
   *
   */
  status: z.string(),
  dateOfBirth: z.string(),
  gender: z.string(),
  idNumber: z.string(),
  dependencyType: DependencyTypeSchema,
  /**
   * Can be any of these in any possible casing:
   *
   * ```
   * 'mr' | 'mrs' | 'miss' | 'dr' | 'mnr' | 'mev' | 'mej'
   * ```
   */
  title: z.string(),
  initials: z.string(),
  membershipEffectiveDateFrom: z.string(),
  membershipToDate: z.string(),
  benefitStartDate: z.string(),
  joinDate: z.string(),
  leftDate: z.string(),
  recordDate: z.string(),
});

export type PatientDetail = z.infer<typeof PatientDetailSchema>;
export type ResponseDetails = z.infer<typeof ResponseDetailsSchema>;

export const FamilyRecordSchema = z.object({
  patientDetails: PatientDetailSchema.array(),
});

export const FamilyCheckResponseSchema = z.object({
  responseDetails: ResponseDetailsSchema,
  familyRecord: FamilyRecordSchema,
});

export type FamilyCheckResponse = z.infer<typeof FamilyCheckResponseSchema>;

export type FamilyRecord = z.infer<typeof FamilyRecordSchema>;

const AdministratorsResponseSchema = AdministratorSchema.array();
export type AdministratorsResponse = z.infer<
  typeof AdministratorsResponseSchema
>;

const AdministratorsRequestSchema = z.object({});
export type AdministratorsRequest = z.infer<typeof AdministratorsRequestSchema>;

export type SubmitClaimRequest = z.infer<typeof Claim.CreateClaimRequestSchema>;
export type SubmitClaimResponse = z.infer<
  typeof Claim.CreateClaimResponseSchema
>;

export type SubmitClaimReversalRequest = z.infer<
  typeof CreateClaimReversalRequestSchema
>;
export type SubmitClaimReversalResponse = z.infer<
  typeof CreateClaimReversalResponseSchema
>;

export type ClaimResponseModelCreateRequest = z.infer<
  typeof ClaimResponseSchema
>;

export const SubmitClaimRequestSchema = CreateClaimRequestSchema;
export const SubmitClaimReversalRequestSchema =
  CreateClaimReversalRequestSchema;

const ResultSuccessSchema = z.object({
  type: z.literal('success'),
  data: CreateClaimSuccessResponseSchema,
});

const ResultErrorSchema = z.object({
  type: z.literal('error'),
  error: z.string(),
});

export const SubmitClaimResultSchema = z.discriminatedUnion('type', [
  ResultSuccessSchema,
  ResultErrorSchema,
]);

export type SubmitClaimResult = z.infer<typeof SubmitClaimResultSchema>;

const ReversalSuccessSchema = z.object({
  type: z.literal('success'),
  data: CreateClaimReversalSuccessResponseSchema,
});

export const SubmitClaimReversalResultSchema = z.discriminatedUnion('type', [
  ReversalSuccessSchema,
  ResultErrorSchema,
]);

export type SubmitClaimReversalResult = z.infer<
  typeof SubmitClaimReversalResultSchema
>;
